<template>
  <div v-if="visible" class="h-screen w-screen bg-black/25 backdrop-blur inset-0 fixed z-40">
    <div class="flex flex-col justify-center items-center w-full h-full">
      <div class="max-w-lg w-full p-6">
        <div class="bg-gray-100 bg-border p-2 rounded-lg mb-4 flex items-center justify-between dark:bg-gray-900">
          <div />

          <div class="flex items-center space-x-2">
            <o-button outline flat @click="visible = false">
              {{ $t('general.cancel') }}
            </o-button>
            <o-button variant="primary" @click="handlePrint">
              {{ $t('general.print') }}
            </o-button>
          </div>
        </div>
      </div>

      <div class="flex-grow flex-shrink w-full h-10">
        <div class="w-full h-full max-w-xs mx-auto">
          <o-scroll>
            <div id="receipt-content" class="receipt text-left w-full text-sm">
              <div class="bg-white p-4 overflow-auto dark:bg-gray-900">
                <div class="text-center">
                  <img v-if="logo" :src="logo" :alt="title" class="mb-3 h-10 inline-block">
                  <h2 class="text-base font-semibold" v-html="title" />
                  <p class="">
                    {{ phone }}
                  </p>
                  <p class="">
                    {{ address }}
                  </p>
                </div>
                <div class="mt-6 mb-6 text-center">
                  <div class="text-xl mb-1">
                    {{ $t('components.giftCard.actions.printReceipt.giftCard', { number: giftCard.displayNumber }) }}
                  </div>

                  <div class="text-base">
                    {{ $t('components.giftCard.actions.printReceipt.balance', { balance: giftCard.balanceLabel }) }}
                  </div>

                  <div v-if="giftCard.expires" class="text-base">
                    {{ $t('components.giftCard.actions.printReceipt.expires', { expiry: giftCard.expiryDateLabel }) }}
                  </div>

                  <template v-if="giftCard.customer">
                    {{ $t('components.giftCard.actions.printReceipt.customer', { name: giftCard.customer.displayName }) }}
                  </template>

                  <template v--if="giftCard.gift">
                    <div class="my-12">
                      <div v-if="giftCard.gift && giftCard.gift.to && giftCard.gift.to.name">
                        {{ $t('components.giftCard.actions.printReceipt.to', { name: giftCard.gift.to.name }) }}
                      </div>

                      <div v-if="giftCard.gift && giftCard.gift.message" class="mb-2">
                        {{ giftCard.gift.message }}
                      </div>

                      <div v-if="giftCard.gift && giftCard.gift.from && giftCard.gift.from.name" class="mb-2">
                        {{ $t('components.giftCard.actions.printReceipt.from', { name: giftCard.gift.from.name }) }}
                      </div>
                    </div>
                  </template>
                </div>

                <div v-if="giftCard.transactions.length">
                  <p class="font-medium mb-2 text-center">
                    {{ $t('general.transactions') }}
                  </p>

                  <div class="flex items-center justify-between">
                    <div class="w-1/2">
                      {{ $t('general.type') }}
                    </div>
                    <div class="w-1/2 text-right">
                      {{ $t('general.amount') }}
                    </div>
                  </div>

                  <div v-for="transaction in giftCard.transactions" :key="transaction.id" class="flex items-center justify-between">
                    <div class="w-1/2">
                      {{ transaction.typeLabel }}
                    </div>
                    <div class="w-1/2 text-right">
                      {{ transaction.priceLabel }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </o-scroll>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { GiftCard } from '~/_api/models/giftCard'

interface Props {
  giftCard: GiftCard
}

const props = defineProps<Props>()

const { $auth, $print } = useNuxtApp()

const visible = ref(false)

const currencySymbol = computed(() => {
  return (props.giftCard as any).data.currencySymbol
})

const location = computed(() => {
  return $auth.info.location
})

const title = computed(() => {
  return location.value.name
})

const logo = computed(() => {
  return location.value.images?.small
})

const address = computed(() => {
  return `${location.value.address}\n${location.value.city} ${location.value.region} ${location.value.postcode}\n${location.value.country}`
})

const phone = computed(() => {
  return location.value.phone
})

const open = () => {
  visible.value = true
}

const handlePrint = () => {
  const content = document.getElementById('receipt-content')?.innerHTML
  if (content) {
    $print(content)
  }
}

defineExpose({ open })
</script>

<style lang="scss">
$triangle-size: 4px; // Define once and use everywhere

@mixin receipt-background($color) {
  $stop1: $triangle-size * 1.42;
  $stop2: $triangle-size * 0.7;
  $stop1r: $stop1 + 0.01;
  $stop2r: $stop2 + 0.01;

  background:
    linear-gradient(135deg, transparent $stop1, $color $stop1r) top left,
    linear-gradient(45deg, $color $stop2, transparent $stop2r) top left,
    linear-gradient(135deg, $color $stop2, transparent $stop2r) bottom left,
    linear-gradient(45deg, transparent $stop1, $color $stop1r) bottom left;
  background-repeat: repeat-x;
  background-size: ($triangle-size * 2) $triangle-size;
  padding: $triangle-size 0;
}

.receipt {
  @include receipt-background(white);

  &-list {
    background-color: white;
  }
}

.dark .receipt {
  @include receipt-background(var(--color-gray-900));

  &-list {
    background-color: var(--color-gray-900);
  }
}

</style>
